/**
 * Court packing algorithm for auto-assignment.
 * Picks the best court(s) to minimize fragmentation.
 */

interface CourtCandidate {
  id: string;
  sort_order?: number;
}

interface BookingSlot {
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

/**
 * Pick the best courts to assign for a booking.
 *
 * Scoring per court (only bookings on the same date as requestedStart):
 * 1. Adjacent booking bonus (+3): a booking ends exactly at requestedStart
 *    OR starts exactly at requestedEnd
 * 2. Same-day booking count (+1 per booking on that date)
 * 3. Tiebreaker: lowest sort_order wins
 *
 * If count > 1, prefer courts with adjacent sort_order
 * (Bana 1+2 over Bana 1+3).
 */
export function pickBestCourts(
  availableCourts: CourtCandidate[],
  bookings: BookingSlot[],
  requestedStart: Date,
  requestedEnd: Date,
  count: number = 1,
): string[] {
  if (availableCourts.length === 0) return [];
  if (count <= 0) return [];

  const reqStartMs = requestedStart.getTime();
  const reqEndMs = requestedEnd.getTime();

  // Only consider non-cancelled bookings on the same date
  const reqDateStr = requestedStart.toISOString().slice(0, 10);
  const dayBookings = bookings.filter((b) => {
    if (b.status === "cancelled") return false;
    return b.start_time.slice(0, 10) === reqDateStr;
  });

  // Filter out courts that have a conflicting booking at the requested time
  const freeCourts = availableCourts.filter((court) => {
    return !dayBookings.some((b) => {
      if (b.court_id !== court.id) return false;
      const bStart = new Date(b.start_time).getTime();
      const bEnd = new Date(b.end_time).getTime();
      return bStart < reqEndMs && bEnd > reqStartMs;
    });
  });

  if (freeCourts.length < count) return []; // Not enough courts

  // Score each free court
  const scored = freeCourts.map((court) => {
    let score = 0;
    const courtBookings = dayBookings.filter((b) => b.court_id === court.id);

    for (const b of courtBookings) {
      const bStart = new Date(b.start_time).getTime();
      const bEnd = new Date(b.end_time).getTime();

      // Adjacent bonus: booking ends exactly at our start
      if (bEnd === reqStartMs) score += 3;
      // Adjacent bonus: booking starts exactly at our end
      if (bStart === reqEndMs) score += 3;

      // Same-day count
      score += 1;
    }

    return {
      id: court.id,
      score,
      sortOrder: court.sort_order ?? 999,
    };
  });

  // Sort by score DESC, then sort_order ASC
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.sortOrder - b.sortOrder;
  });

  if (count === 1) {
    return [scored[0].id];
  }

  // For count > 1: try to find adjacent sort_order groups
  // Build all combinations of `count` courts and pick the one with:
  // 1. Highest total score
  // 2. Smallest sort_order spread (adjacent courts preferred)
  if (count <= 3 && scored.length <= 20) {
    let bestCombo: typeof scored = [];
    let bestTotalScore = -1;
    let bestSpread = Infinity;

    // Simple combination search for small count
    const indices = Array.from({ length: scored.length }, (_, i) => i);
    const combos = getCombinations(indices, count);

    for (const combo of combos) {
      const courts = combo.map((i) => scored[i]);
      const totalScore = courts.reduce((sum, c) => sum + c.score, 0);
      const orders = courts.map((c) => c.sortOrder).sort((a, b) => a - b);
      const spread = orders[orders.length - 1] - orders[0];

      if (
        totalScore > bestTotalScore ||
        (totalScore === bestTotalScore && spread < bestSpread)
      ) {
        bestTotalScore = totalScore;
        bestSpread = spread;
        bestCombo = courts;
      }
    }

    return bestCombo.map((c) => c.id);
  }

  // Fallback for large sets: just take top N
  return scored.slice(0, count).map((c) => c.id);
}

/** Generate all combinations of size k from array */
function getCombinations(arr: number[], k: number): number[][] {
  if (k === 1) return arr.map((v) => [v]);
  const result: number[][] = [];
  for (let i = 0; i <= arr.length - k; i++) {
    const rest = getCombinations(arr.slice(i + 1), k - 1);
    for (const combo of rest) {
      result.push([arr[i], ...combo]);
    }
  }
  return result;
}

/**
 * Check how many courts are available for a given time slot.
 */
export function countAvailableCourts(
  courts: CourtCandidate[],
  bookings: BookingSlot[],
  slotStart: Date,
  slotEnd: Date,
): number {
  const startMs = slotStart.getTime();
  const endMs = slotEnd.getTime();

  return courts.filter((court) => {
    return !bookings.some((b) => {
      if (b.court_id !== court.id) return false;
      if (b.status === "cancelled") return false;
      const bStart = new Date(b.start_time).getTime();
      const bEnd = new Date(b.end_time).getTime();
      return bStart < endMs && bEnd > startMs;
    });
  }).length;
}
